import axios from "axios";
import { API_URL } from "constants";

export const createFileCategory = async (formData, companyId) => {
  const url = `${API_URL}/${companyId}/files/create-file-category`;
  return axios.post(url, formData).then((response) => response.data);
};

export const updateFileCategory = async (formData, companyId) => {
  const url = `${API_URL}/${companyId}/files/update-file-category/${formData.id}`;
  return axios.patch(url, formData).then((response) => response.data);
};

export const fetchFileCategoryById = async (
  companyId,
  id,
  source,
  options = {}
) => {
  let url = `${API_URL}/${companyId}/files/fetch-file-category-by-id/${id}`;
  if (source !== undefined && source !== null && source !== "") {
    url += `/source/${source}`;
  }
  const { page, pageSize, dateFrom, dateTo, userId, customerId, isSigned, includeSubcategories, textSearch, excludeFileIds, lightweight } = options || {};
  const params = {};
  if (includeSubcategories) params.includeSubcategories = "true";
  if (typeof textSearch === "string" && textSearch.trim() !== "") params.textSearch = textSearch.trim();
  if (Array.isArray(excludeFileIds) && excludeFileIds.length) params.excludeFileIds = excludeFileIds.join(",");
  if (lightweight) params.lightweight = "true";
  if (Number.isFinite(page) && page > 0) {
    params.page = page;
  }
  if (Number.isFinite(pageSize) && pageSize > 0) {
    params.pageSize = pageSize;
  }
  const toDateString = (v) => {
    if (v == null) return "";
    if (typeof v === "string") return v.trim();
    if (v instanceof Date && !Number.isNaN(v.getTime()))
      return v.toISOString().split("T")[0];
    return "";
  };
  const dateFromStr = toDateString(dateFrom);
  const dateToStr = toDateString(dateTo);
  if (dateFromStr !== "") params.dateFrom = dateFromStr;
  if (dateToStr !== "") params.dateTo = dateToStr;
  if (userId && String(userId).trim() !== "") params.userId = String(userId).trim();
  if (customerId && String(customerId).trim() !== "") params.customerId = String(customerId).trim();
  if (isSigned === true || isSigned === "true") params.isSigned = "true";
  else if (isSigned === false || isSigned === "false") params.isSigned = "false";
  const config = Object.keys(params).length ? { params } : undefined;
  return axios.get(url, config).then((response) => response.data);
};

export const fetchTreeFilesCategory = async (companyId, source) => {
  const url = `${API_URL}/${companyId}/files/fetch-tree-file-categories/source/${source}`;
  return axios.get(url).then((response) => response.data);
};

/**
 * Every category of the company, flat (id, title, parentCategory, order) and
 * without file counts — what the customer card's document tree needs to name
 * the parent categories that hold no files of their own.
 */
export const fetchAllFileCategories = async (companyId) => {
  const url = `${API_URL}/${companyId}/files/fetch-file-categories`;
  return axios.get(url).then((response) => response.data);
};

export const fetchFileById = async (fileId, companyId) => {
  const url = `${API_URL}/${companyId}/files/${fileId}`;
  return axios.get(url).then((response) => response.data);
};

export const fetchFilesCategories = async (companyId, userId) => {
  const url = `${API_URL}/${companyId}/files/fetch-tree-file-categories/${userId}`;
  return axios.get(url).then((response) => response.data);
};

export const assignFileCategoryPermissionToUser = async (
  companyId,
  formData
) => {
  const url = `${API_URL}/${companyId}/users/file-category/post`;
  return axios.post(url, formData).then((response) => response.data);
};

export const fetchFiles = (companyId, filtersValue, source) => {
  const url = `${API_URL}/${companyId}/files/source/${source}/fetch`;

  return axios.post(url, { filtersValue }).then((response) => response.data);
};

export const fetchFileCategorySignatureTypes = async (
  companyId,
  categoryId
) => {
  const url = `${API_URL}/${companyId}/files/fetch-file-category-by-id/${categoryId}/signature-types`;
  return axios.get(url).then((response) => response.data);
};

export const setCategoryAsFavorite = async (companyId, categoryId) => {
  const url = `${API_URL}/${companyId}/files/categories/${categoryId}/favourite`;
  return axios.post(url).then((response) => response.data);
};

export const fetchFavoriteCategories = async (
  companyId,
  subject = "customer"
) => {
  const url = `${API_URL}/${companyId}/files/categories-fetch-favourite/${subject}`;
  return axios.get(url).then((response) => response.data);
};

export const fetchCustomerFiles = async (
  companyId,
  customerId,
  filtersValue = {}
) => {
  const url = `${API_URL}/${companyId}/customers/${customerId}/files`;
  return axios.post(url, { filtersValue }).then((response) => response.data);
};

/**
 * Unsigned assigned files for the current user.
 * Without options → full array (legacy; used by header badge + reminder refresh).
 * With { page } → { entries, pagination, filterOptions } for the unsigned-files page.
 */
export const fetchUnsignedAssignedFiles = async (options) => {
  const opts = options && typeof options === "object" ? options : {};
  const params = {};

  if (opts.page !== undefined) {
    params.page = opts.page;
    if (opts.pageSize !== undefined) {
      params.pageSize = opts.pageSize;
    }
    if (opts.textSearch?.trim()) {
      params.textSearch = opts.textSearch.trim();
    }
    if (opts.companyId?.trim()) {
      params.companyId = opts.companyId.trim();
    }
    if (opts.signType?.trim()) {
      params.signType = opts.signType.trim();
    }
  }

  const url = `${API_URL}/files/unsigned-assigned`;
  const config = Object.keys(params).length ? { params } : undefined;
  return axios.get(url, config).then((response) => response.data);
};

/**
 * Cancel every pending signature request on a document at once (the
 * "no longer relevant" action of the unsigned-files list). Collected
 * signatures are kept.
 *
 * Answers 200 even when nothing could be removed, so callers must read
 * `removed` / `failed` instead of trusting the status code.
 *
 * @param {string} companyId
 * @param {string} fileId
 * @returns {Promise<import("types/Files").CancelSigningResult>}
 */
export const cancelFileSigning = async (companyId, fileId) => {
  const url = `${API_URL}/${companyId}/files/${fileId}/cancel-signing`;
  return axios.post(url).then((response) => response.data);
};

export const fetchExpiringAssignedFiles = async (companyId, params = {}) => {
  const url = `${API_URL}/${companyId}/files/expiring-assigned`;
  return axios.get(url, { params }).then((response) => response.data);
};

/**
 * «Sutvarkyta»: takes an expiring document off «Baigiasi galiojimas» (the assigned person or a
 * Master of its company). `companyId` is the document's own company, `dateTo` the end date the
 * person saw; the server answers 409 when the document is no longer like that.
 */
export const markExpiryHandled = async (companyId, fileId, dateTo) => {
  const url = `${API_URL}/${companyId}/files/${fileId}/expiry-handled`;
  return axios.post(url, { dateTo }).then((response) => response.data);
};
